import { MarketingDrawer } from '@app/components/general'
import { metaSetter } from '@app/utils'
import { Header } from '@app/components/general/header'
import { SectionContainer } from '@app/components/stateless/containers/section-container'
import { FC, PropsWithChildren } from 'react'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { Paragraph } from '@app/components/stateless/typo/paragraph'
import Trans from 'next-translate/Trans'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'
import { blueLink } from '@app/components/stateless/typo/link-style'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/web-accessibility',
    }),
  }
}

const checklistLinks = [
  { href: 'https://a11yproject.com/checklist', name: 'access-links.0' },
  {
    name: 'access-links.1',
    href: 'https://medium.com/@krisrivenburgh/the-ada-checklist-website-compliance-guidelines-for-2019-in-plain-english-123c1d58fad9',
  },
  {
    name: 'access-links.2',
    href: 'https://webflow.com/accessibility/checklist',
  },
  {
    name: 'access-links.3',
    href: 'https://developer.mozilla.org/en-US/docs/Web/Accessibility',
  },
  {
    name: 'access-links.4',
    href: 'https://www.section508.gov/test/testing-overview/',
  },
]

const linkCss = `${blueLink} font-medium`
const h3Styles =
  'text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100'
const listStyle = 'space-y-2 list-disc px-6 py-2'

const BlockContainer: FC<PropsWithChildren> = ({ children }) => (
  <div className='py-2'>{children}</div>
)

const PBlock: FC<PropsWithChildren> = ({ children }) => (
  <p className='pb-2 text-gray-800 dark:text-gray-200'>{children}</p>
)

function WebAccessibility() {
  const { t } = useTranslation('web-accessibility')
  const { t: tt } = useTranslation('web-accessibility-links')

  return (
    <MarketingDrawer navPosition='relative'>
      <SectionContainer container block>
        <Header>{t('header')}</Header>
        <Paragraph className='pb-2 md:w-2/3'>{t('header-details')}</Paragraph>
        <h2 className='text-lg md:text-xl py-2 font-semibold'>
          {t('curated-list')}
        </h2>
        <Paragraph>{t('curated-list-d')}</Paragraph>
        <BlockContainer>
          <h3 className={h3Styles}>{t('colors-safe')}</h3>
          <Trans
            i18nKey='web-accessibility:colors-safe-d'
            components={[
              <PBlock key={'accessp-1'} />,
              <a
                className={linkCss}
                href={'https://material.io/design/color/#'}
                target='_blank'
                rel='noreferrer'
                key={'mat-link-1'}
              />,
              <a
                className={linkCss}
                href={'https://contrast-ratio.com/'}
                target='_blank'
                rel='noreferrer'
                key={'contrast-link-2'}
              />,
            ]}
          />
        </BlockContainer>
        <BlockContainer>
          <h3 className={h3Styles}>{t('wcag-principles')}</h3>
          <PBlock>{t('wcag-principles-d')}</PBlock>
          <ul className={listStyle}>
            <li>
              <a
                className={linkCss}
                target='_blank'
                href={
                  'https://www.w3.org/WAI/fundamentals/accessibility-principles/'
                }
                rel='noreferrer'
              >
                {tt('wcag-links.0')}
              </a>
            </li>
            <li>
              <a
                className={linkCss}
                target='_blank'
                href={'https://www.w3.org/WAI/standards-guidelines/wcag/'}
                rel='noreferrer'
              >
                {tt('wcag-links.1')}
              </a>
            </li>
            <li>
              <a
                className={linkCss}
                target='_blank'
                href={'https://www.w3.org/TR/WCAG21/'}
                rel='noreferrer'
              >
                {tt('wcag-links.2')}
              </a>
            </li>
          </ul>
        </BlockContainer>
        <BlockContainer>
          <h3 className={h3Styles}>{t('access-checklist')}</h3>
          <PBlock>{t('access-checklist-d')}</PBlock>
          <ul className={listStyle}>
            {checklistLinks.map(({ href, name }: any) => (
              <li key={href}>
                <a
                  className={linkCss}
                  href={href}
                  target='_blank'
                  rel='noreferrer'
                >
                  {tt(name)}
                </a>
              </li>
            ))}
          </ul>
        </BlockContainer>
        <BlockContainer>
          <h3 className={h3Styles}>{t('features')}</h3>
          <PBlock>{t('features-details')}</PBlock>
          <ul className={listStyle}>
            <li>
              <a
                className={linkCss}
                href={'https://www.apple.com/accessibility/iphone/vision/'}
                target='_blank'
                rel='noreferrer'
              >
                {tt('features-links.0')}
              </a>
            </li>
            <li>
              <a
                className={linkCss}
                href={'https://www.google.com/accessibility/'}
                target='_blank'
                rel='noreferrer'
              >
                {tt('features-links.1')}
              </a>
            </li>
            <li>
              <a
                className={linkCss}
                href={
                  'https://business.twitter.com/en/blog/our-accessibility-team-shares-best-practices.html'
                }
                target='_blank'
                rel='noreferrer'
              >
                {tt('features-links.2')}
              </a>
            </li>
            <li>
              <a
                href={'https://ebay.gitbook.io/mindpatterns/'}
                className={linkCss}
                target='_blank'
                rel='noreferrer'
              >
                {tt('features-links.3')}
              </a>
            </li>
          </ul>
        </BlockContainer>

        <BlockContainer>
          <h3 className={h3Styles}>{t('react')}</h3>
          <PBlock>{t('react-details')}</PBlock>
          <ul className={listStyle}>
            <li>
              <a
                className={linkCss}
                href={'https://chakra-ui.com'}
                target='_blank'
                rel='noreferrer'
              >
                {tt('react-links.0')}
              </a>
            </li>
            <li>
              <a
                className={linkCss}
                href={'https://react-icons.github.io/react-icons/icons?name=gr'}
                rel='noreferrer'
                target='_blank'
              >
                {tt('react-links.1')}
              </a>
            </li>
            <li>
              <a
                className={linkCss}
                href={'https://inclusive-components.design/'}
                rel='noreferrer'
                target='_blank'
              >
                {tt('react-links.2')}
              </a>
            </li>
          </ul>
        </BlockContainer>

        <BlockContainer>
          <h3 className={h3Styles}>{t('avoid-animations')}</h3>
          <PBlock>{t('avoid-animations-d')}</PBlock>
        </BlockContainer>

        <BlockContainer>
          <h3 className={h3Styles}>{t('perf-impact')}</h3>
          <Trans
            i18nKey='web-accessibility:perf-impact'
            components={[<PBlock key={'pblock-1'} />, <em key={'que-1'} />]}
          />
        </BlockContainer>

        <BlockContainer>
          <h3 className={h3Styles}>{t('practice-voiceover')}</h3>
          <PBlock>{t('practice-voiceover-d')}</PBlock>
          <ul className={listStyle}>
            <li>
              <a
                className={linkCss}
                href={
                  'https://support.apple.com/guide/iphone/turn-on-and-practice-voiceover-iph3e2e415f/ios'
                }
                target='_blank'
                rel='noreferrer'
              >
                {tt('voiceover-links.0')}
              </a>
            </li>
            <li>
              <a
                className={linkCss}
                href={
                  'https://support.google.com/accessibility/android/answer/6283677?hl=en'
                }
                target='_blank'
                rel='noreferrer'
              >
                {tt('voiceover-links.1')}
              </a>
            </li>
          </ul>
        </BlockContainer>

        <BlockContainer>
          <h4 className='text-xl font-medium'>{t('more-resources')}</h4>
          <ul className={listStyle}>
            <li>
              <a
                className={linkCss}
                href={'https://a11yresources.webflow.io'}
                target='_blank'
                rel='noreferrer'
              >
                {tt('resources-links.0')}
              </a>
            </li>
            <li>
              <a
                className={linkCss}
                href={'https://www.a11ytips.dev'}
                target='_blank'
                rel='noreferrer'
              >
                {tt('resources-links.1')}
              </a>
            </li>
            <li>
              <a
                className={linkCss}
                href={'https://www.getstark.co'}
                target='_blank'
                rel='noreferrer'
              >
                {tt('resources-links.2')}
              </a>
            </li>
          </ul>
        </BlockContainer>
      </SectionContainer>
    </MarketingDrawer>
  )
}

export default metaSetter(
  { WebAccessibility },
  {
    translation: 'web-accessibility',
  }
)
